import { Injectable } from '@angular/core';
import { PoComboFilter, PoComboOption } from '@po-ui/ng-components';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { GitService } from '../../../core/api/git.service';
import { GitFileMatch } from '../../../core/interfaces/git.interface';
import { getApiErrorMessage } from '../../../core/utils/http-error-message.util';

/**
 * Busca o fonte no repositório git pelo NOME, sem diferenciar maiúsculas de minúsculas.
 *
 * Existe porque o campo anterior era texto livre e exigia o caminho completo: quem pede o
 * review sabe `CRMA010.PRW`, não o diretório em que o fonte foi parar, e um caminho ou uma
 * caixa errada devolviam "não foi possível listar o histórico" — indistinguível, para quem
 * olha, de o arquivo não existir.
 *
 * A busca vai ao servidor a cada termo, e não uma vez só como a de repositórios: um
 * repositório tem milhares de arquivos, e trazer a árvore inteira para filtrar no navegador
 * pagaria a listagem completa para descobrir um nome.
 */
@Injectable()
export class GitFileComboFilterService implements PoComboFilter {
  /** Slug do repositório escolhido no passo anterior. */
  repoSlug = '';

  /** Branch escolhida. Vazia deixa o backend resolver a padrão do repositório. */
  ref = '';

  /**
   * Avisa o passo sobre o resultado da última busca: mensagem quando falha, string vazia
   * quando dá certo.
   *
   * O contrato do PoComboFilter só admite devolver opções, então um erro viraria lista
   * vazia — indistinguível de "nenhum arquivo com esse nome". A falha precisa chegar à
   * tela por fora.
   */
  notify: ((error: string) => void) | null = null;

  /**
   * Arquivos já devolvidos, por caminho. O combo entrega de volta apenas o valor
   * escolhido, e o passo seguinte precisa do caminho EXATO — que é justamente o que a
   * busca resolve e o que não pode ser reconstruído a partir do que foi digitado.
   */
  private readonly byValue = new Map<string, GitFileMatch>();

  constructor(private gitService: GitService) {}

  /**
   * O termo digitado chega em `params.value` — contrato do PoComboFilter. Ler outro nome
   * faz o filtro ser sempre vazio.
   */
  getFilteredData(params: { property?: string; value?: string }): Observable<PoComboOption[]> {
    const term = (params.value || '').trim();
    if (!this.repoSlug || !term) {
      return of([]);
    }
    return this.search(term);
  }

  /**
   * Reabre um valor já escolhido — o combo chama isto ao voltar de um passo.
   *
   * Resolve pelo que a busca guardou, sem ir ao servidor: o caminho exato já está em mãos,
   * e uma busca aqui poderia devolver outro arquivo de nome parecido.
   */
  getObjectByValue(value: string): Observable<PoComboOption> {
    const known = this.byValue.get(value);
    if (known) {
      return of(optionFor(known));
    }
    return of({ label: value, value });
  }

  /** Arquivo escolhido, com o caminho exato do repositório. */
  fileFor(value: string): GitFileMatch | null {
    return this.byValue.get(value) ?? null;
  }

  /** Esquece o que foi carregado. Chamado ao trocar de repositório ou de branch. */
  clear(): void {
    this.byValue.clear();
  }

  private search(term: string): Observable<PoComboOption[]> {
    return this.gitService.searchFiles(this.repoSlug, term, this.ref).pipe(
      tap(files => {
        files.forEach(file => this.byValue.set(file.path, file));
        this.notify?.('');
      }),
      map(files => files.map(optionFor)),
      catchError(err => {
        this.notify?.(getApiErrorMessage(err, 'Não foi possível buscar arquivos no repositório.'));
        return of([] as PoComboOption[]);
      })
    );
  }
}

/**
 * O rótulo mostra o nome e o caminho: dois arquivos de mesmo nome em diretórios
 * diferentes são a razão de a busca existir, e sem o caminho eles ficariam idênticos na
 * lista. O valor é o caminho exato, que é o que segue para o backend.
 */
function optionFor(file: GitFileMatch): PoComboOption {
  const name = (file.name || '').trim();
  const path = (file.path || '').trim();
  return { label: name && name !== path ? `${name} — ${path}` : path, value: path };
}
